import {
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { ResourceType, BackupPolicyType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { CloudProviderIndicator } from '@/components/cloudProviderIndicator';
import { SelectPlaceholder } from '@/components/selectPlaceholder';
import type { StepProps } from '@/components/wizard/StepProps';
import { validateName } from '@/components/wizard/renameEntityName';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { ResourceTypes } from '@/data/inventory/resourceTypes';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import {
  getDefaultBigQueryPitrSchedules,
  getDefaultHighFrequencySchedules,
  getDefaultStandardSchedules,
} from './defaultBackupPolicy';
import { SupportedResourceTypesText } from './supportedResourceTypesText';
import { useBackupPolicyAvailableTypes } from './useBackupPolicyAvailableTypes';

// todo: this might be redundant and we can use only pitrPolicySupportedResourceTypes inline instead of this entire mechanism.
const usePolicyLabelSupportedResourceTypes = (
  labelType: PolicyLabelType
): ResourceType[] | undefined => {
  const {
    EC2HighFrequencyBackupEnabled,
    RdsPitrEnabled,
    SapHanaPitrSupport,
    AzureSqlVirtualMachineSupport,
    EnableBigQueryNativePolicy,
  } = useFeatureFlags();
  const HighFrequencySupportedResourceTypes: ResourceType[] = [
    ...(EC2HighFrequencyBackupEnabled ? [ResourceType.AwsEc2] : []),
    ...[
      ResourceType.AwsDynamoDb,
      ResourceType.AwsS3,
      ResourceType.GcpCloudStorageBucket,
    ],
  ];

  const pitrPolicySupportedResourceTypes: ResourceType[] = [
    ...(RdsPitrEnabled ? [ResourceType.AwsRds] : []),
    ...(AzureSqlVirtualMachineSupport
      ? [ResourceType.AzureSqlVirtualMachine]
      : []),
    ...(SapHanaPitrSupport
      ? [ResourceType.AzureSapHanaVm, ResourceType.GcpSapHanaVm]
      : []),
    ...(EnableBigQueryNativePolicy ? [ResourceType.GcpBigQuery] : []),
  ];

  return labelType === 'high-frequency'
    ? HighFrequencySupportedResourceTypes
    : labelType === 'pitr'
      ? pitrPolicySupportedResourceTypes
      : undefined;
};

const getPolicyType = (
  labelType: PolicyLabelType,
  destination: 'backup-vault' | 'source-account',
  resourceTypes?: ResourceType[]
) => {
  let policyType: BackupPolicyType =
    BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD;
  if (labelType === 'high-frequency') {
    policyType = BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY;
  } else if (labelType === 'pitr') {
    if (resourceTypes?.includes(ResourceType.AwsRds)) {
      policyType = BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR;
    } else if (resourceTypes?.includes(ResourceType.GcpBigQuery)) {
      policyType = BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR;
    } else {
      policyType = BackupPolicyType.BACKUP_POLICY_TYPE_PITR;
    }
  } else if (labelType === 'standard') {
    policyType =
      destination === 'backup-vault'
        ? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD
        : BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD;
  }
  return policyType;
};

const policiesWithSingleResourceType = [
  BackupPolicyType.BACKUP_POLICY_TYPE_PITR,
  BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR,
  BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR,
];

export const BackupPolicyNameSelection = (
  props: StepProps<BackupPolicyEditState>
) => {
  const [policyTypeLabel, setPolicyTypeLabel] =
    useState<PolicyLabelType>('standard');
  const [destination, setDestination] = useState<
    'backup-vault' | 'source-account'
  >('backup-vault');
  const { FSxSupport, AwsRedshiftNativeBackupSupport } = useFeatureFlags();
  const next = () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };
  const availableResourceTypes = useBackupPolicyAvailableTypes();
  const policyLabelSupportedResourceTypes =
    usePolicyLabelSupportedResourceTypes(policyTypeLabel);

  return (
    <Stack sx={{ height: '100%' }} alignItems='center' justifyContent='center'>
      <Stack width='400px' gap='40px'>
        <PolicyNameInput {...props} />
        <PolicyTypeSelection
          selectedLabelType={policyTypeLabel}
          setSelectedLabelType={(labelType: PolicyLabelType) => {
            setPolicyTypeLabel(labelType);
            let policyType: BackupPolicyType = getPolicyType(
              labelType,
              destination
            );
            let resourceTypes = policiesWithSingleResourceType.includes(
              policyType
            )
              ? availableResourceTypes?.[policyType]?.length
                ? [availableResourceTypes?.[policyType]?.[0]]
                : undefined
              : undefined;

            if (
              !resourceTypes &&
              policyLabelSupportedResourceTypes?.length === 1
            ) {
              resourceTypes = [policyLabelSupportedResourceTypes[0]];
              policyType = getPolicyType(labelType, destination, resourceTypes);
            }

            const schedules =
              policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
                ? getDefaultHighFrequencySchedules()
                : policyType ===
                    BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR
                  ? getDefaultBigQueryPitrSchedules()
                  : getDefaultStandardSchedules();
            props.setWizardState({
              ...props.wizardState,
              policyType,
              schedules,
              resourceTypes,
            });
          }}
        />
        {(FSxSupport || AwsRedshiftNativeBackupSupport) &&
          policyTypeLabel === 'standard' && (
            <PolicyDestinationSelection
              selectedDestination={destination}
              setSelectedDestination={(destination) => {
                setDestination(destination);
                props.setWizardState({
                  ...props.wizardState,
                  policyType: getPolicyType(policyTypeLabel, destination),
                });
              }}
            />
          )}
        {(policyTypeLabel === 'high-frequency' ||
          policyTypeLabel === 'standard') && (
          <Stack gap='4px' direction='row' alignItems='center'>
            <Typography padding='12px 0px' align='center'>
              Available for
            </Typography>
            <SupportedResourceTypesText
              resourceTypes={
                availableResourceTypes?.[props.wizardState.policyType] ?? []
              }
            />
          </Stack>
        )}
        {policyTypeLabel === 'pitr' &&
          (policyLabelSupportedResourceTypes?.length ?? 0) > 1 && (
            <ResourceTypeSelection
              availableResourceTypes={policyLabelSupportedResourceTypes ?? []}
              resourceType={props.wizardState.resourceTypes?.[0] ?? undefined}
              onChange={(resourceType) => {
                const resourceTypes = resourceType ? [resourceType] : undefined;
                const newPolicyType = getPolicyType(
                  policyTypeLabel,
                  destination,
                  resourceTypes
                );
                const schedules =
                  newPolicyType ===
                  BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR
                    ? getDefaultBigQueryPitrSchedules()
                    : getDefaultStandardSchedules();
                props.setWizardState({
                  ...props.wizardState,
                  resourceTypes,
                  policyType: newPolicyType,
                  schedules,
                });
              }}
            />
          )}
        <Button
          data-testid='create-backup-policy-name-continue-btn'
          variant='contained'
          disabled={
            !validateName(props.wizardState.name) ||
            (props.wizardState.resourceTypes &&
              !props.wizardState.resourceTypes.length) ||
            props.wizardState.policyType ===
              BackupPolicyType.BACKUP_POLICY_TYPE_UNSPECIFIED
          }
          onClick={next}
          sx={{ height: '48px' }}
        >
          Continue
          <i className='material-symbols-arrow-right-alt ml-[8px]' />
        </Button>
      </Stack>
    </Stack>
  );
};

const PolicyNameInput = (props: StepProps<BackupPolicyEditState>) => {
  return (
    <Stack gap='16px'>
      <FormControl size='small' className='flex-1'>
        <FormLabel>Name</FormLabel>
        <OutlinedInput
          inputProps={{ 'data-testid': 'create-backup-policy-name-input' }}
          autoFocus
          sx={{
            padding: '8px',
          }}
          placeholder='Use a descriptive name, such as "Production with PII"'
          onChange={(e) =>
            props.setWizardState({
              ...props.wizardState,
              name: e.target.value,
            })
          }
          value={props.wizardState.name}
        />
      </FormControl>
    </Stack>
  );
};

type PolicyLabelType = 'standard' | 'high-frequency' | 'pitr';
const usePolicyTypeLabels = (): Partial<
  Record<
    PolicyLabelType,
    {
      label: string;
      description: string;
      supportedPolicyTypes: BackupPolicyType[];
    }
  >
> => {
  const {
    enableHourlyBackup,
    AzureSqlVirtualMachineSupport,
    SapHanaPitrSupport,
    RdsPitrEnabled,
    EnableBigQueryNativePolicy,
  } = useFeatureFlags();

  const pitrSupportedPolicyTypes: BackupPolicyType[] = [
    ...(AzureSqlVirtualMachineSupport || SapHanaPitrSupport
      ? [BackupPolicyType.BACKUP_POLICY_TYPE_PITR]
      : []),
    ...(RdsPitrEnabled
      ? [BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR]
      : []),
    ...(EnableBigQueryNativePolicy
      ? [BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR]
      : []),
  ];

  return {
    standard: {
      supportedPolicyTypes: [
        BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD,
        BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD,
      ],
      label: 'Standard',
      description: 'Backup runs up to 4 times a day',
    },
    ...(!enableHourlyBackup
      ? {
          'high-frequency': {
            supportedPolicyTypes: [
              BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY,
            ],
            label: 'High frequency',
            description: `Backup runs as often as every 15 minutes`,
          },
        }
      : {}),
    ...(pitrSupportedPolicyTypes.length > 0
      ? {
          pitr: {
            supportedPolicyTypes: pitrSupportedPolicyTypes,
            label: 'Point-in-time recovery',
            description: `PITR recovery runs continuously`,
          },
        }
      : {}),
  };
};

const PolicyTypeSelection = ({
  selectedLabelType,
  setSelectedLabelType,
}: {
  selectedLabelType: PolicyLabelType;
  setSelectedLabelType: (type: PolicyLabelType) => void;
}) => {
  const policyTypeLabels = usePolicyTypeLabels();

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Type</FormLabel>
      <Select
        sx={{ paddingLeft: '8px' }}
        size='medium'
        value={selectedLabelType}
        onChange={(event) =>
          setSelectedLabelType(event.target.value as PolicyLabelType)
        }
        displayEmpty
        renderValue={(value: PolicyLabelType) => {
          return policyTypeLabels[value]?.label;
        }}
      >
        {Object.entries(policyTypeLabels).map(([key, value]) => (
          <MenuItem key={key} value={key}>
            <Stack gap='4px' padding='4px'>
              <Typography variant='body1'>{value.label}</Typography>
              <Typography variant='subtitle2' color='text.secondary'>
                {value.description}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
const PolicyDestinationSelection = ({
  selectedDestination,
  setSelectedDestination,
}: {
  selectedDestination: 'backup-vault' | 'source-account';
  setSelectedDestination: (
    destination: 'backup-vault' | 'source-account'
  ) => void;
}) => {
  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Store snapshots in</FormLabel>
      <Select
        sx={{ paddingLeft: '8px' }}
        size='medium'
        value={selectedDestination || 'backup-vault'}
        onChange={(event) =>
          setSelectedDestination(
            event.target.value as 'backup-vault' | 'source-account'
          )
        }
        displayEmpty
        renderValue={(value: 'backup-vault' | 'source-account') => {
          return value === 'backup-vault' ? 'Vault' : 'Source account';
        }}
      >
        <MenuItem value='backup-vault'>
          <Stack gap='4px' padding='4px'>
            <Typography variant='body1'>Vault</Typography>
            <Typography variant='subtitle2' color='text.secondary'>
              Snapshots are stored in your managed vaults.
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem value='source-account'>
          <Stack gap='4px' padding='4px'>
            <Typography variant='body1'>Source account</Typography>
            <Typography variant='subtitle2' color='text.secondary'>
              Snapshots remain in the source account. Managed by your cloud
              provider.
            </Typography>
          </Stack>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

const ResourceTypeSelection = ({
  availableResourceTypes,
  resourceType,
  onChange,
}: {
  availableResourceTypes: ResourceType[];
  resourceType?: ResourceType;
  onChange: (resourceType: ResourceType) => void;
}) => {
  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Apply policy to</FormLabel>
      <Select
        sx={{ paddingLeft: '8px' }}
        size='medium'
        value={resourceType ?? ResourceType.ResourceTypeUnspecified}
        onChange={(event) => onChange(event.target.value as ResourceType)}
        displayEmpty
        renderValue={(value: ResourceType) => {
          if (!value || value === ResourceType.ResourceTypeUnspecified) {
            return <SelectPlaceholder title='Select a resource type' />;
          }
          return (
            <CloudProviderIndicator
              cloudProvider={ResourceTypes[value]?.provider}
              text={ResourceTypes[value]?.displayName}
            />
          );
        }}
      >
        {availableResourceTypes.map((resourceType) => (
          <MenuItem key={resourceType} value={resourceType}>
            <CloudProviderIndicator
              cloudProvider={ResourceTypes[resourceType]?.provider}
              text={ResourceTypes[resourceType]?.displayName}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
